import React from "react";
import { AvatarChip } from "../AvatarChip";

/**
 * ReportsSection — admin moderation inbox for player reports.
 * Renders OPEN / DISMISSED / ACTIONED / ALL tabs and per-report actions.
 */
export function ReportsSection({ data, tab, setTab, decide, restore, decisionBusy }) {
  return (
    <>
      <div className="flex gap-1 mb-4" data-testid="admin-report-tabs">
        {[
          { id: "open",       label: `OPEN · ${data.counts.open || 0}` },
          { id: "dismissed",  label: `DISMISSED · ${data.counts.dismissed || 0}` },
          { id: "actioned",   label: `ACTIONED · ${data.counts.actioned || 0}` },
          { id: "all",        label: "ALL" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${
              tab === t.id ? "btn-neon" : "btn-ghost"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="admin-reports-list">
        {data.reports.length === 0 ? (
          <p className="text-[12px] text-white/45 text-center py-12" data-testid="admin-reports-empty">
            No reports in this bucket.
          </p>
        ) : data.reports.map((r) => (
          <div
            key={r.report_id}
            data-testid={`admin-report-${r.report_id}`}
            className={`glass rounded-2xl p-4 border ${
              r.status === "open" ? "border-[#14b8a6]/40"
              : r.status === "actioned" ? "border-[#14b8a6]/40"
              : "border-white/15"
            }`}
          >
            <div className="flex items-center gap-3">
              <AvatarChip avatarId={r.reported_avatar_id} pfpUrl={r.reported_pfp_url} size={48} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold">
                  <i className="fas fa-flag mr-1" />{r.reason_label}
                </p>
                <p className="font-bold text-sm">
                  Reported: <span className="text-white">{r.reported_name || r.reported_user_id}</span>
                </p>
                <p className="text-[11px] text-white/60">
                  By <b>{r.reporter_name || r.reporter_user_id}</b>
                  {" · "}{r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                </p>
              </div>
              <span
                data-testid={`admin-report-status-${r.report_id}`}
                className={`text-[9px] tracking-widest px-2 py-1 rounded-full ${
                  r.status === "open" ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                  : r.status === "actioned" ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                  : "bg-white/10 text-white/70 border border-white/15"
                }`}
              >
                {r.status.toUpperCase()}
              </span>
            </div>
            {r.details && (
              <p className="mt-3 p-2.5 rounded-lg bg-black/40 border border-white/10 text-[12px] text-white/75 whitespace-pre-wrap">
                "{r.details}"
              </p>
            )}
            {r.moderator_note && (
              <p className="mt-2 text-[11px] text-[#a3a3ac]">
                <i className="fas fa-pen-to-square mr-1" />Mod note: {r.moderator_note}
              </p>
            )}
            {r.image_kind && (
              <p className="mt-2 text-[11px] text-[#14b8a6]">
                <i className="fas fa-image mr-1" />Image report · <b>{r.image_kind.replace("_", " ").toUpperCase()}</b>
                {r.target_team_id ? <> · team <code className="text-[10px]">{r.target_team_id}</code></> : null}
              </p>
            )}
            {r.status === "open" ? (
              <div className="flex flex-wrap justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => decide(r.report_id, "dismiss")}
                  disabled={decisionBusy === r.report_id}
                  data-testid={`admin-report-dismiss-${r.report_id}`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
                >
                  DISMISS
                </button>
                {r.image_kind && (
                  <button
                    type="button"
                    onClick={() => decide(r.report_id, "strip_image")}
                    disabled={decisionBusy === r.report_id}
                    data-testid={`admin-report-strip-image-${r.report_id}`}
                    className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/30 disabled:opacity-60"
                  >
                    🖼️ STRIP IMAGE
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => decide(r.report_id, "remove_player")}
                  disabled={decisionBusy === r.report_id}
                  data-testid={`admin-report-remove-${r.report_id}`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6] text-black font-bold hover:brightness-110 disabled:opacity-60"
                >
                  💥 SUSPEND PLAYER
                </button>
              </div>
            ) : r.status === "actioned" ? (
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={() => restore(r.reported_user_id)}
                  data-testid={`admin-report-restore-${r.report_id}`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                >
                  RESTORE PLAYER
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
}
